/*
  What part of the organisation somebody may see, as a form.

  THE VOCABULARY IS `memberScopeSchema`, which is `peopleFilterSchema` minus three fields — the same
  words the assistant's `findPeople` uses. One vocabulary rather than two means the scope an owner
  grants and the group a model can name are evaluated by one predicate, `matchesPeopleFilter`, which
  cannot drift from itself.

  VALUES COME FROM THE PROJECT'S OWN ROSTER, not from a free-text box, and that is the difference
  between a control and a trap. An owner typing "Finanace" produces a grant that matches nobody and
  looks exactly like a working restriction: the member sees an empty project and the owner sees a
  scope that was clearly saved. Offering the values that exist makes that unreachable for every field
  where a fixed set exists.

  THE MATCHED COUNT IS COMPUTED IN THE BROWSER, live, from the roster this owner already holds. No
  round trip, no preview endpoint, and — the part that matters — no chance of the number disagreeing
  with what the server will do, because it is the same `matchesPeopleFilter` over the same people.
  Showing it is 95% of a "view as this member" preview for 2% of the surface, and an owner who can see
  that a scope matches three people has been told the thing they needed before they granted it.
*/
import { useMemo, useState } from 'react';
import { inScope, type Person } from '@integrate/core';

/** The fields a scope may name, in the order somebody thinks about them. */
const TEXT_FIELDS = [
  { key: 'levelValue', label: 'Part of the hierarchy' },
  { key: 'businessUnit', label: 'Business unit' },
  { key: 'country', label: 'Country' },
  { key: 'location', label: 'Location' },
  { key: 'legalEntity', label: 'Legal entity' },
  { key: 'seniority', label: 'Grade' },
] as const;

type ScopeDraft = Record<string, string>;

/** Every value this project actually has for a field, so nothing offered matches nobody. */
function valuesFor(people: readonly Person[], key: string): string[] {
  const seen = new Set<string>();
  for (const person of people) {
    if (key === 'levelValue') {
      for (const value of Object.values(person.levels ?? {})) if (value) seen.add(value);
      continue;
    }
    const value = (person as unknown as Record<string, unknown>)[key];
    if (typeof value === 'string' && value !== '') seen.add(value);
  }
  return [...seen].sort();
}

export function ScopeEditor({
  people,
  scope,
  salaryHidden,
  disabled,
  onSave,
}: {
  /** The whole roster, which an owner always has — owners are never scoped. */
  people: readonly Person[];
  scope: Record<string, unknown> | null;
  salaryHidden: boolean;
  disabled: boolean;
  onSave(next: { scope: Record<string, unknown> | null; salaryHidden: boolean }): void;
}): JSX.Element {
  const [draft, setDraft] = useState<ScopeDraft>(() => {
    const out: ScopeDraft = {};
    for (const { key } of TEXT_FIELDS) {
      const value = scope?.[key];
      if (typeof value === 'string') out[key] = value;
    }
    return out;
  });
  const [hideSalary, setHideSalary] = useState(salaryHidden);

  const named = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(draft)) if (value !== '') out[key] = value;
    return Object.keys(out).length > 0 ? out : null;
  }, [draft]);

  /*
    The count, over the roster in hand. `inScope` rather than the raw predicate, so this answers the
    same question the server will — including the two `true`s that keep leavers and inactive rows in.
    A preview that quietly excluded them would report a smaller number than the member will see.
  */
  const matched = useMemo(
    () => (named === null ? people.length : people.filter((p) => inScope(p, named as never)).length),
    [named, people],
  );

  return (
    <div className="scope-editor" data-testid="scope-editor">
      <p className="muted small">
        Leave every field blank for access to the whole organisation. Fields combine — naming a
        business unit and a country means people in both.
      </p>

      {TEXT_FIELDS.map((field) => {
        const options = valuesFor(people, field.key);
        return (
          <div className="field" key={field.key}>
            <label htmlFor={`scope-${field.key}`}>{field.label}</label>
            <select
              id={`scope-${field.key}`}
              data-testid={`scope-${field.key}`}
              value={draft[field.key] ?? ''}
              disabled={disabled}
              onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))}
            >
              <option value="">Any</option>
              {options.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        );
      })}

      <label className="check">
        <input
          type="checkbox"
          data-testid="scope-hide-salary"
          checked={hideSalary}
          disabled={disabled}
          onChange={(e) => setHideSalary(e.target.checked)}
        />
        Do not show compensation
      </label>

      {/*
        WHAT THIS GRANT ACTUALLY COVERS, before it is granted. A scope naming nobody is the failure
        this line exists to make visible: it is indistinguishable on the server from a working
        restriction, and the member on the other end simply sees an empty project.
      */}
      <p className={matched === 0 ? 'bad small' : 'muted small'} data-testid="scope-matched">
        {named === null
          ? `The whole organisation — ${people.length} people.`
          : matched === 0
            ? 'This matches nobody. Somebody given it would see an empty project.'
            : `This matches ${matched} of ${people.length} people.`}
      </p>

      <button
        className="primary"
        data-testid="scope-save"
        disabled={disabled}
        onClick={() => onSave({ scope: named, salaryHidden: hideSalary })}
      >
        Save access
      </button>
    </div>
  );
}
